/* Where the buying and selling happened, for drawing on a price line.
 *
 * The point of putting these on a chart is to answer "did I buy that well" with
 * something other than a number: a fill halfway up a run looks different from
 * one at the top, and no amount of average-cost arithmetic shows it.
 *
 * Two sources, and they are not the same kind of thing:
 *
 *   - **A recorded trade** is a fill. It has a day, a side, a size and the price
 *     that was paid. It is a fact and it is drawn as one.
 *   - **A lot's opening** is not a fill. `openedAt` with `avgCost` says "this
 *     account started holding this on that day, and the average paid across
 *     however many purchases was that" — which for a position built over six
 *     months is a price that was never traded at, on a day that may not be any
 *     of the days it was bought.
 *
 * So the second is only produced where the first does not exist for an account,
 * because otherwise it is a worse copy of information already drawn, and it is
 * marked as an opening rather than a purchase so nobody reads an average as a
 * fill. Nothing here invents a date: an account with neither a trade nor an
 * opening produces nothing.
 *
 * ── and why the chart does not plot the second one ────────────────────────
 *
 * `openedAt` is frequently not a purchase date at all. A book seeded from a
 * snapshot of what was already held carries the day it was written down on
 * every lot in it — twelve companies bought across two years all claiming to
 * have been opened on the same afternoon. Drawn on a price line that is a
 * confident mark in the wrong place, which is worse than no mark: it invites
 * exactly the reading "so that is where I bought", about a date nobody
 * recorded.
 *
 * What *is* known about such a lot is its average cost. So the chart draws that
 * — as a horizontal line, which claims a price and no date — and leaves the
 * opening to the list below it, where it appears as the record it is, with the
 * date on show and a button to correct it. `openingsOnly` is the test the chart
 * uses to decide. */

import type { Account, Holding, Trade } from '../types';

export interface Marker {
  /** The trade this came from, where it came from one. Absent on an opening,
      which is a lot's own record and is corrected in the lot editor. */
  tradeId?: string;
  /** ISO day. */
  at: string;
  kind: 'buy' | 'sell' | 'opened';
  shares: number;
  /** Native currency of the listing. An average for `opened`, a fill otherwise. */
  price: number;
  account: Account;
  /** Sells only, and only where it was recorded. */
  realizedGain?: number;
}

export function markersFor(
  ticker: string,
  trades: Trade[],
  holdings: Holding[],
): Marker[] {
  const mine = trades.filter((t) => t.ticker === ticker);
  const out: Marker[] = mine.map((t) => {
    const m: Marker = {
      tradeId: t.id,
      at: t.at.slice(0, 10),
      kind: t.side,
      shares: t.shares,
      price: t.price,
      account: t.account,
    };
    if (t.realizedGain !== undefined) m.realizedGain = t.realizedGain;
    return m;
  });

  /* An account whose buying was never written down still has an opening date
     and an average. Better a marked estimate than a chart that says nothing
     happened. */
  const accountsWithBuys = new Set(mine.filter((t) => t.side === 'buy').map((t) => t.account));
  for (const h of holdings) {
    if (h.ticker !== ticker) continue;
    if (accountsWithBuys.has(h.account)) continue;
    if (h.avgCost <= 0) continue;   // no cost on record is not a price to draw
    out.push({
      at: h.openedAt.slice(0, 10),
      kind: 'opened',
      shares: h.shares,
      price: h.avgCost,
      account: h.account,
    });
  }

  return out.sort((a, b) => a.at.localeCompare(b.at) || a.kind.localeCompare(b.kind));
}

/** Only the fills — the marks whose date and price are both on record, and so
    the only ones a price line may carry as points. */
export function fills(markers: Marker[]): Marker[] {
  return markers.filter((m) => m.kind !== 'opened');
}

/** The first day worth drawing: a little before the earliest mark, so the fill
    does not sit on the very edge. Null when there is nothing to show. */
export function earliestMark(markers: Marker[]): string | null {
  const first = markers[0]?.at;
  if (!first) return null;
  const d = new Date(`${first}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() - 21);
  return d.toISOString().slice(0, 10);
}

/* What the markers add up to, for the line under the chart.
 *
 * Three counts, not two. Buys and sells are kept apart because netting them
 * hides a position that was traded around; openings are kept apart from both
 * because an opening is not a recorded purchase — counting its shares as
 * "bought" would report a fill that nobody wrote down. */
export function markerTotals(markers: Marker[]): {
  buys: number; sells: number; opened: number;
  bought: number; sold: number; openedShares: number;
  realised: number | null;
} {
  let buys = 0, sells = 0, opened = 0;
  let bought = 0, sold = 0, openedShares = 0;
  let realised = 0;
  let anyRealised = false;

  for (const m of markers) {
    if (m.kind === 'sell') {
      sells++;
      sold += m.shares;
      if (m.realizedGain !== undefined) { realised += m.realizedGain; anyRealised = true; }
    } else if (m.kind === 'buy') {
      buys++;
      bought += m.shares;
    } else {
      opened++;
      openedShares += m.shares;
    }
  }
  return {
    buys, sells, opened, bought, sold, openedShares,
    realised: anyRealised ? realised : null,
  };
}
